import React, { useState } from "react";
import {
  Alert,
  Box,
  Button as MuiButton,
  Dialog,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LockOpen } from "lucide-react";
import { useForm } from "react-hook-form";
import { login } from "../../redux/slice/authSlice";
import { useAppDispatch } from "../../redux/slice/redux-hooks";
import { FAILED_CODE } from "../../service/ServiceConstants";
import CustomPasswordTextField from "./CustomPasswordTextField";
import CustomTextField from "./CustomTextField";
import { useModalStyles } from "./useModalStyles";

type ExternalAccessSignInDialogProps = {
  onClose: () => void;
  email: string;
  allowEmailEdit?: boolean;
  title: string;
  description: string;
  submitButtonLabel: string;
  onAuthorized: () => void;
};

const ExternalAccessSignInDialog: React.FC<ExternalAccessSignInDialogProps> = ({
  onClose,
  email,
  allowEmailEdit = false,
  title,
  description,
  submitButtonLabel,
  onAuthorized,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const {
    tokens,
    paperSx,
    headerSx,
    iconBoxSx,
    bodySx,
    footerSx,
    closeIconSx,
    inputFieldSx,
    primaryBtnSx,
  } = useModalStyles();

  const { control, handleSubmit: handleFormSubmit } = useForm({
    defaultValues: {
      email: email || "",
      password: "",
    },
  });
  const dispatch = useAppDispatch();
  const [errorMessageContent, setErrorMessageContent] = useState("");
  const [showError, setShowError] = useState(false);

  const onSubmit = (data: { email: string; password: string }) => {
    handleLogin(data.email, data.password);
  };

  const handleLogin = async (userName: string, passwordValue: string) => {
    if (!userName || !passwordValue) {
      setErrorMessageContent("Please enter both email and password.");
      setShowError(true);
      return;
    }

    if (!/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i.test(userName)) {
      setErrorMessageContent("Invalid email address.");
      setShowError(true);
      return;
    }

    try {
      const response = await dispatch(
        login({
          loginInfo: { userName, password: passwordValue },
          interceptorOption: { skipErrorPopup: true },
        })
      ).unwrap();

      if (response) {
        onAuthorized();
        onClose();
      }
    } catch (e: unknown) {
      const err = e as { code?: string; message?: string } | undefined;
      if (err?.code === FAILED_CODE) {
        setErrorMessageContent(err?.message || "Login failed.");
      } else {
        setErrorMessageContent("Unknown error. Please contact support.");
      }
      setShowError(true);
    }
  };

  return (
    <Dialog
      open={true}
      onClose={(_, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          onClose();
        }
      }}
      disableEscapeKeyDown
      maxWidth="xs"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{ sx: paperSx(isMobile) }}
    >
      {/* Header */}
      <Box sx={headerSx}>
        <Box sx={iconBoxSx(tokens.accent)}>
          <LockOpen color={tokens.accent} size={22} />
        </Box>
        <Box>
          <Typography
            fontWeight={700}
            sx={{ fontSize: "1.1rem", color: tokens.textPrimary, lineHeight: 1.3 }}
          >
            {title}
          </Typography>
          <Typography sx={{ fontSize: "0.8rem", color: tokens.textSecondary, mt: 0.15 }}>
            {description}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={closeIconSx}>
          <CloseIcon sx={{ fontSize: "1rem" }} />
        </IconButton>
      </Box>

      {/* Body */}
      <Box component="form" onSubmit={handleFormSubmit(onSubmit)} noValidate sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
        <Box sx={bodySx}>
          {errorMessageContent && showError && (
            <Alert variant="filled" severity="error" onClose={() => setShowError(false)} sx={{ borderRadius: "10px" }}>
              {errorMessageContent}
            </Alert>
          )}

          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: tokens.textSecondary, mb: 0.8 }}>
              Email
            </Typography>
            <Box sx={inputFieldSx()}>
              <CustomTextField
                control={control}
                controllerName="email"
                label=""
                placeholder="Enter your email"
                isDisabled={!allowEmailEdit}
                rules={allowEmailEdit ? {
                  required: "Email is required.",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
                    message: "Invalid email.",
                  },
                } : undefined}
              />
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontSize: "0.8rem", fontWeight: 600, color: tokens.textSecondary, mb: 0.8 }}>
              Password
            </Typography>
            <Box sx={inputFieldSx()}>
              <CustomPasswordTextField
                control={control}
                fullWidth
                controllerName="password"
                label=""
                placeholder="Enter your password"
                id="password"
                autoComplete="current-password"
                rules={{ required: "Password is required." }}
              />
            </Box>
          </Box>
        </Box>

        {/* Footer */}
        <Box sx={footerSx}>
          <MuiButton
            fullWidth
            variant="contained"
            type="submit"
            sx={primaryBtnSx}
          >
            {submitButtonLabel}
          </MuiButton>
        </Box>
      </Box>
    </Dialog>
  );
};

export default ExternalAccessSignInDialog;
